import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";

import AdminRoutes from "Routes/Components/Admin";

import { HTTPErrorCallback } from "Helpers/Error";
import { toDateTime, transformIntoArrayOptions } from "Helpers/Format";

import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseNotification from "Hooks/UseNotification";
import UseServices from "Hooks/UseServices";

import Loading from "Components/Admin/Loading";
import Section from "Components/Admin/Section";
import Table from "Components/Admin/Table";
import { TableActionLink, withRowLink } from "Components/Admin/TableRowLink";

import LinkOverview from "../Components/LinkOverview";
import LinkStatusSelect, {
  LinkStatusContext,
} from "../Components/LinkStatusSelect";
import PreSelection from "../Components/PreSelection";
import SeletivaForm from "../Components/SeletivaForm";
import * as S from "./styles";

// ⚠️ O `value` é chave de estado e NÃO muda com o renome do rótulo.
// "Projetos" virou "Projetos Apresentados" em 18/08/2026: o nome passou a dizer
// o que a aba sempre fez — ela lista apenas vínculos de "Apresentado" em diante
// (flag show_in_selective_projects).
const tabs = [
  { text: "Dados", value: "dados" },
  { text: "Projetos Apresentados", value: "projetos" },
  { text: "Pré-seleção de Projetos", value: "pre-selecao" },
];

const baseColumns = [
  { Header: "Nº", accessor: "project_id" },
  { Header: "Projeto", accessor: "project_name" },
  { Header: "Proponente", accessor: "proponent" },
  { Header: "Status do Projeto", accessor: "project_status" },
];

const linkedAtColumn = {
  Header: "Vinculado em",
  accessor: (row) => (row.created_at ? toDateTime(row.created_at) : ""),
};

const PROJECT_LINK_TITLE = "Abrir projeto — Ctrl/Cmd+clique para nova aba";

// ⚠️ `project_id`, e não `id`: nesta lista o `id` é o do VÍNCULO
// (project_sponsors), não o do projeto.
const projectPath = (row) =>
  row.project_id
    ? AdminRoutes.UpdateProjects.path.replace(":id", row.project_id)
    : null;

// Colunas do CSV: status do vínculo como texto simples (mesmo fallback da aba
// de pré-seleção, para a coluna não sair vazia).
//
// A coluna "Interesse do Patrocinador" foi retirada desta aba (tela e CSV): a
// resposta do patrocinador é acompanhada no detalhe do projeto — aqui ela ficava
// quase sempre em "Em avaliação", sem informar nada.
const downloadColumns = [
  ...baseColumns,
  {
    Header: "Status da Associação",
    accessor: (row) => row.link_status || "Pré selecionado",
  },
  linkedAtColumn,
];

const UpdateSeletiva = () => {
  const { id } = useParams();
  const { api } = UseServices();
  const { showNotification } = UseNotification();
  const { loading, setLoading } = UseGlobalLoading();

  const [initialData, setInitialData] = useState(null);
  const [selectedTab, setSelectedTab] = useState("dados");
  const [linkedProjects, setLinkedProjects] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  useEffect(() => {
    if (!id) return;

    getSeletiva();
    getLinkedProjects();
    // eslint-disable-next-line
  }, [id]);

  // Opções de status do vínculo (mesma fonte usada no detalhe do projeto).
  useEffect(() => {
    api
      .get("/projects/get-project-sponsor-statuses")
      .then(({ data }) =>
        setStatusOptions(transformIntoArrayOptions(data, "id", "display_name")),
      )
      .catch((error) => HTTPErrorCallback(error));
    // eslint-disable-next-line
  }, []);

  const getSeletiva = () => {
    api
      .post("/select-projects/get-by-id", { id })
      .then(({ data }) => setInitialData(data.select_project))
      .catch((error) => HTTPErrorCallback(error));
  };

  const getLinkedProjects = () => {
    api
      .post("/select-projects/get-projects", { id })
      .then(({ data }) => setLinkedProjects(data.projects || []))
      .catch((error) => HTTPErrorCallback(error));
  };

  // Atualiza a linha localmente após gravar o novo status (sem recarregar).
  const onLinkStatusSaved = useCallback((sponsorId, newStatusId, label) => {
    setLinkedProjects((prev) =>
      prev.map((project) =>
        project.project_sponsor_id === sponsorId
          ? {
              ...project,
              status_id: newStatusId,
              link_status: label ?? project.link_status,
            }
          : project,
      ),
    );
  }, []);

  const linkStatusCtx = useMemo(
    () => ({ statusOptions, onSaved: onLinkStatusSaved }),
    [statusOptions, onLinkStatusSaved],
  );

  const submit = (payload) => {
    setLoading(true);

    api
      .post("/select-projects/update", payload)
      .then(() => {
        setLoading(false);
        showNotification(
          "success",
          "Sucesso!",
          "Seletiva atualizada com sucesso!",
        );
        // Não redireciona para a listagem: apenas atualiza os dados da tela
        // (o refresh de initialData repopula o formulário).
        getSeletiva();
      })
      .catch((error) => {
        setLoading(false);
        return HTTPErrorCallback(error);
      });
  };

  return (
    <Section>
      <S.Wrapper>
        {!initialData ? (
          <Loading />
        ) : (
          <>
            <S.Tabs>
              {tabs.map((tab) => (
                <S.Tab
                  key={tab.value}
                  type="button"
                  $active={selectedTab === tab.value}
                  onClick={() => setSelectedTab(tab.value)}
                >
                  {tab.text}
                </S.Tab>
              ))}
            </S.Tabs>

            {selectedTab === "dados" && (
              // `linkedProjects` já vem filtrado pelo backend por
              // show_in_selective_projects — é exatamente a lista de projetos
              // "Apresentado" em diante, que a guarda de publicação consulta.
              <SeletivaForm
                initialData={initialData}
                onSubmit={submit}
                submitLabel={loading ? "Salvando..." : "Salvar Alterações"}
                loading={loading}
                presentedProjectsCount={linkedProjects.length}
                onPresentationGenerated={getSeletiva}
              />
            )}

            {selectedTab === "projetos" && (
              <S.TableWrapper>
                <LinkStatusContext.Provider value={linkStatusCtx}>
                  <Table
                    name="Projetos Apresentados da Seletiva"
                    data={linkedProjects}
                    columns={[
                      // O link entra só aqui: `baseColumns` é compartilhado com
                      // `downloadColumns`, e o CSV não pode receber JSX.
                      ...withRowLink(baseColumns, {
                        accessor: "project_name",
                        to: projectPath,
                        rowId: (row) => row.project_id,
                      }),
                      {
                        Header: "Status da Associação",
                        accessor: "link_status",
                        Cell: ({ cell }) => (
                          <LinkStatusSelect
                            sponsorId={cell.row.original.project_sponsor_id}
                            statusId={cell.row.original.status_id}
                            fallbackLabel={cell.row.original.link_status}
                          />
                        ),
                      },
                      linkedAtColumn,
                      {
                        Header: "Ações",
                        accessor: "actions",
                        noSearch: true,
                        width: "70px",
                        Cell: ({ cell }) => (
                          <TableActionLink
                            to={projectPath(cell.row.original)}
                            title={PROJECT_LINK_TITLE}
                          />
                        ),
                      },
                    ]}
                    downloadColumns={downloadColumns}
                    disableDownloadButton={false}
                    // Visão POR VÍNCULO (A14 — D21): a linha expande num painel
                    // somente leitura com o que o patrocinador vê; a edição é
                    // no projeto (link dentro do painel). Múltiplas linhas
                    // podem ficar expandidas ao mesmo tempo.
                    renderRowSubComponent={({ row }) => (
                      <LinkOverview
                        projectSponsorId={row.original.project_sponsor_id}
                      />
                    )}
                  />
                </LinkStatusContext.Provider>
              </S.TableWrapper>
            )}

            {selectedTab === "pre-selecao" && (
              // statusOptions vêm daqui para não repetir o fetch do catálogo:
              // a aba também edita o status do vínculo inline.
              <PreSelection
                id={id}
                onLinked={getLinkedProjects}
                statusOptions={statusOptions}
              />
            )}
          </>
        )}
      </S.Wrapper>
    </Section>
  );
};

export default UpdateSeletiva;
